import { useEffect, useRef, useState } from "preact/hooks"
import { requestPasswordReset, resetPassword } from "./lib/api"
import { PublicShell } from "./PublicShell"

// The two halves of password reset, the near-twin of VerifyEmail.tsx.
//
// ForgotPassword is /forgot: type the address, get a link. ResetPassword is
// /reset?token=…, the page the emailed link opens. Both are public routes —
// someone who has forgotten their password has no session to offer, and the
// link is routinely opened on a different device from the one that asked for
// it. Neither makes a session-backed API call: those 401 signed out and the
// fetch wrapper turns a 401 into a redirect to /login, which would bounce the
// visitor straight off the page. Both endpoints here are auth endpoints, so
// they go through raw fetch and a 4xx stays on the page.

export function ForgotPassword() {
	const emailRef = useRef<HTMLInputElement>(null)
	useEffect(() => {
		emailRef.current?.focus()
	}, [])

	const [email, setEmail] = useState("")
	const [sent, setSent] = useState(false)
	const [error, setError] = useState("")
	const [loading, setLoading] = useState(false)

	async function handleSubmit(e: Event) {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			await requestPasswordReset(email)
			setSent(true)
		} catch (err) {
			// A 429 lands here with the server's own sentence ("too many
			// password reset requests — try again later"), which is already the
			// thing to say.
			setError(err instanceof Error ? err.message : "Something went wrong")
		} finally {
			setLoading(false)
		}
	}

	return (
		<PublicShell>
			<h1>Forgot your password?</h1>

			{sent ? (
				<div class="stack gap-3">
					{/* Says "if there's an account", not "we sent it": the server
					    answers the same either way on purpose, so promising mail
					    for an address that has no account would be the
					    account-existence oracle the endpoint just avoided. */}
					<p class="muted">
						If there's a Quizfish account for <strong>{email}</strong>, a link
						to choose a new password is on its way. It expires in an hour.
					</p>
					<p class="muted small">
						Nothing in your inbox after a minute? Look in spam, and check the
						address above is the one you signed up with.
					</p>
					<div>
						<a href="/login" class="btn primary">
							Back to log in
						</a>
					</div>
				</div>
			) : (
				<div class="stack gap-3">
					<p class="muted">
						Type the email address you signed up with and we'll send you a link
						to choose a new one.
					</p>
					<form onSubmit={handleSubmit} class="stack gap-3">
						<div class="field">
							<label for="email">Email</label>
							<input
								id="email"
								ref={emailRef}
								type="email"
								autocomplete="email"
								required
								value={email}
								onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
							/>
						</div>

						{error && <div class="alert danger">{error}</div>}

						<div class="flex wrap center gap-3">
							<button type="submit" class="primary" disabled={loading}>
								{loading ? "Please wait…" : "Email me a link"}
							</button>
							<a class="small" href="/login">
								Back to log in
							</a>
						</div>
					</form>
				</div>
			)}
		</PublicShell>
	)
}

export function ResetPassword() {
	// Read once, on mount: the token is in the URL the mail opened, and
	// nothing on this page changes it.
	const [token] = useState(
		() => new URLSearchParams(window.location.search).get("token") ?? "",
	)

	const passwordRef = useRef<HTMLInputElement>(null)
	useEffect(() => {
		passwordRef.current?.focus()
	}, [])

	const [password, setPassword] = useState("")
	const [confirm, setConfirm] = useState("")
	const [done, setDone] = useState(false)
	const [error, setError] = useState("")
	const [loading, setLoading] = useState(false)

	async function handleSubmit(e: Event) {
		e.preventDefault()
		setError("")
		if (password !== confirm) {
			setError("The two passwords do not match")
			return
		}
		setLoading(true)
		try {
			await resetPassword(token, password)
			setDone(true)
		} catch (err) {
			// Everything the server refuses arrives here as a sentence to
			// render: an expired, invalid or already-used link, a password
			// under the minimum, or the rate limit.
			setError(err instanceof Error ? err.message : "Something went wrong")
		} finally {
			setLoading(false)
		}
	}

	if (token === "") {
		return (
			<PublicShell>
				<h1>That link did not work</h1>
				<p class="muted">
					This link is missing its reset code. Ask for a fresh one and open the
					newest email — a new link retires the old one.
				</p>
				<div>
					<a href="/forgot" class="btn primary">
						Send me a new link
					</a>
				</div>
			</PublicShell>
		)
	}

	if (done) {
		return (
			<PublicShell>
				<h1>Password changed</h1>
				{/* The reset ended every session this account had, everywhere —
				    that is the point of it, so say so rather than let someone
				    wonder why their other tab logged out. */}
				<p class="muted">
					Log in with your new password. Anywhere you were still logged in has
					been signed out.
				</p>
				<div>
					<a href="/login" class="btn primary">
						Log in
					</a>
				</div>
			</PublicShell>
		)
	}

	return (
		<PublicShell>
			<h1>Choose a new password</h1>
			<form onSubmit={handleSubmit} class="stack gap-3">
				<div class="field">
					<label for="new-password">New password</label>
					<input
						id="new-password"
						ref={passwordRef}
						type="password"
						autocomplete="new-password"
						// Mirrors minPasswordLength in auth.go, which checks it
						// again server-side — this is the hint, not the rule.
						minLength={6}
						required
						value={password}
						onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
					/>
					<div class="hint">At least 6 characters.</div>
				</div>

				<div class="field">
					<label for="confirm-password">Confirm new password</label>
					<input
						id="confirm-password"
						type="password"
						autocomplete="new-password"
						minLength={6}
						required
						value={confirm}
						onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
					/>
				</div>

				{error && <div class="alert danger">{error}</div>}

				<div class="flex wrap center gap-3">
					<button type="submit" class="primary" disabled={loading}>
						{loading ? "Please wait…" : "Set new password"}
					</button>
					<a class="small" href="/forgot">
						Ask for a new link
					</a>
				</div>
			</form>
		</PublicShell>
	)
}
